import { LLMProvider } from './base';

export class GeminiProvider extends LLMProvider {
  constructor(apiKey, config = {}) {
    super(apiKey, config);
    this.model = config.model || 'gemini-2.0-flash-exp';
    this.baseURL = 'https://generativelanguage.googleapis.com/v1beta';
  }

  async chat(messages, systemPrompt = '') {
    return this.retryWithBackoff(async () => {
      const prompt = systemPrompt
        ? `${systemPrompt}\n\n${messages}`
        : messages;

      const response = await fetch(
        `${this.baseURL}/models/${this.model}:generateContent?key=${this.apiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }],
            generationConfig: {
              maxOutputTokens: this.config.maxTokens || 2000,
              temperature: this.config.temperature || 0.7
            }
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Gemini API error');
      }

      const data = await response.json();
      return data.candidates[0].content.parts[0].text;
    });
  }

  async chatMultiTurn(messages, systemPrompt = '') {
    return this.retryWithBackoff(async () => {
      const contents = messages.map(m => ({
        role: m.role === 'user' ? 'user' : 'model',
        parts: [{ text: m.content }]
      }));

      const body = {
        contents,
        generationConfig: {
          maxOutputTokens: this.config.maxTokens || 4096,
          temperature: this.config.temperature || 0.7
        }
      };

      if (systemPrompt) {
        body.systemInstruction = { parts: [{ text: systemPrompt }] };
      }

      const response = await fetch(
        `${this.baseURL}/models/${this.model}:generateContent?key=${this.apiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Gemini API error');
      }

      const data = await response.json();
      return data.candidates[0].content.parts[0].text;
    });
  }

  async chatMultiTurnStream(messages, systemPrompt = '', onChunk = () => {}) {
    const contents = messages.map(m => ({
      role: m.role === 'user' ? 'user' : 'model',
      parts: [{ text: m.content }]
    }));

    const body = {
      contents,
      generationConfig: {
        maxOutputTokens: this.config.maxTokens || 4096,
        temperature: this.config.temperature || 0.7
      }
    };

    if (systemPrompt) {
      body.systemInstruction = { parts: [{ text: systemPrompt }] };
    }

    const response = await fetch(
      `${this.baseURL}/models/${this.model}:streamGenerateContent?alt=sse&key=${this.apiKey}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Gemini API error');
    }

    let fullText = '';
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data: ')) continue;
        const data = trimmed.slice(6);

        try {
          const parsed = JSON.parse(data);
          const text = parsed.candidates?.[0]?.content?.parts?.[0]?.text;
          if (text) {
            fullText += text;
            onChunk(text);
          }
        } catch {
          // skip malformed SSE lines
        }
      }
    }

    return fullText;
  }

  async chatWithImage(images, textPrompt, systemPrompt = '') {
    return this.retryWithBackoff(async () => {
      const imageParts = images.map(img => ({ inlineData: { mimeType: img.mimeType, data: img.base64 } }));
      const body = {
        contents: [{ parts: [...imageParts, { text: textPrompt }] }],
        generationConfig: { maxOutputTokens: 4096, temperature: 0.3 }
      };
      if (systemPrompt) {
        body.systemInstruction = { parts: [{ text: systemPrompt }] };
      }
      const response = await fetch(
        `${this.baseURL}/models/${this.model}:generateContent?key=${this.apiKey}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Gemini API error');
      }
      const data = await response.json();
      return data.candidates[0].content.parts[0].text;
    });
  }

  validateApiKey() {
    return this.apiKey && this.apiKey.length > 20;
  }

  getModelName() {
    return 'Gemini (Google)';
  }
}
